import { PageDataHandler } from "@/lib/utility/data/page-data-handler";
import { AbstractPageDataProvider } from "@/lib/utility/data/page-data-provider.abstract";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { IPageViewModel } from "@/lib/utility/data/page-view-model.interface";
import { log } from "@/lib/utility/log";
import { IPageFilterElement, PageFilterElement } from "@/models/page-filter-element.entity";
import { ISharedContent, SharedContent } from "@/models/shared-content.entity";
import sharedContentService from "@/services/mrfiktiv/services/sharedContentService";
import { MrfiktivSharedContentControllerGetAllForPartnerParamsGen } from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Action, Module, getModule } from "vuex-module-decorators";
import { PaginatedBaseStore } from "../paginated-base.store";
import { SharedContentDataAccessLayer } from "./access-layers/shared-content.access-layer";
import { PaginationFilterListElement } from "./base-pagination.store";
import { IReference } from "@/models/reference.entity";

export class SharedContentPageDataProvider extends AbstractPageDataProvider<
  ISharedContent,
  MrfiktivSharedContentControllerGetAllForPartnerParamsGen
> {
  async getPage(
    query: MrfiktivSharedContentControllerGetAllForPartnerParamsGen
  ): Promise<IPageViewModel<ISharedContent>> {
    const res = await sharedContentService.getSharedContentPaginated(query);

    if (!res.data) {
      res.data = [];
    }

    return { meta: res.meta, data: res.data.map((d) => new SharedContent(d)) };
  }

  getIdentifier(entity: ISharedContent): string {
    return entity.id;
  }
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "sharedContentPaginationStore",
  store
})
export class SharedContentStore extends PaginatedBaseStore<
  ISharedContent,
  MrfiktivSharedContentControllerGetAllForPartnerParamsGen
> {
  _data = SharedContentDataAccessLayer;
  _pageProvider = new SharedContentPageDataProvider();
  _pager = new PageDataHandler<ISharedContent, MrfiktivSharedContentControllerGetAllForPartnerParamsGen>(
    this._data,
    this._pageProvider
  );

  filterOptions: PaginationFilterListElement[] = SharedContent.filterables;

  /**
   * Remove references to a deleted entity from shared content entries.
   * @param param0
   */
  @Action
  async onRefDelete({ refId, partnerId }: { refId: string; partnerId: string }) {
    const activeFilters = this.filters.slice();
    this.setFilters([
      new PageFilterElement({
        key: "refs.refId",
        operation: PageFilterOperationEnum.EQUAL,
        value: refId
      })
    ]);

    const sharedContents = (await this.fetchFirstPage({ partnerId })) ?? [];

    const promises = [];
    for (const sharedContent of sharedContents) {
      const remainingRefs = sharedContent.refs.filter((r) => r.refId !== refId);

      // if no refs remain, invalidate the shared content by remving access
      if (remainingRefs.length === 0) {
        const promise = sharedContent
          .updatePartial({
            refs: remainingRefs,
            validBy: new Date().toISOString()
          })
          .catch(log.error);
        promises.push(promise);
        continue;
      }

      const promise = sharedContent
        .updatePartial({
          refs: remainingRefs
        })
        .catch(log.error);
      promises.push(promise);
    }

    await Promise.all(promises).catch(log.error);

    this.setFilters(activeFilters);
  }

  @Action
  async fetchByRef(data: { partnerId: string; refId: string }) {
    const filter = [
      new PageFilterElement({
        key: "refs.refId",
        operation: PageFilterOperationEnum.EQUAL,
        value: data.refId
      }).dtoPagination
    ];

    const page = await sharedContentService.getSharedContentPaginated({
      partnerId: data.partnerId,
      filter
    });

    const mapped =
      page?.data?.map((d) => {
        const sharedContent = new SharedContent(d);
        this._data.set(sharedContent);
        return sharedContent;
      }) || [];

    return mapped;
  }

  @Action
  async fetchByRefs(data: { partnerId: string; refs: IReference[] }) {
    const filter = data.refs
      .map((r) => [
        new PageFilterElement({
          key: "refs.refId",
          operation: PageFilterOperationEnum.EQUAL,
          value: r.refId
        }).dtoPagination,
        new PageFilterElement({
          key: "refs.refType",
          operation: PageFilterOperationEnum.EQUAL,
          value: r.refType
        }).dtoPagination
      ])
      .flat();

    const page = await sharedContentService.getSharedContentPaginated({
      partnerId: data.partnerId,
      filter
    });

    const mapped =
      page?.data?.map((d) => {
        const sharedContent = new SharedContent(d);
        this._data.set(sharedContent);
        return sharedContent;
      }) || [];

    return mapped;
  }
}

export const SharedContentModule = getModule(SharedContentStore);
